import _ from 'lodash'
import validator from 'validator'
import { isValid, parseISO } from 'date-fns'

import lang from 'lang'

const optionalValidationString = 'optional'

const phoneNumberRegex = /^[0-9a-z +().-]{7,}$/
const numberRegex = /^-?[0-9]+$/

const trim = (value: unknown): unknown => {
  if (_.isString(value)) {
    return value.trim()
  }
  return value
}

type ValidationFn = (field: string, value: unknown) => true | string

const validations: Record<string, ValidationFn> = {
  isArray: (_field, value) => {
    return _.isArray(value) ? true : 'Must be an array'
  },
  email: (_field, value) => {
    if (validator.isEmail(trim(value) as string)) {
      return true
    }
    return lang.validator.email
  },
  nonEmpty: (_field, value) => {
    value = trim(value)
    if (_.isArray(value) && !_.isEmpty(value)) {
      return true
    }
    if (_.isString(value) && !validator.isEmpty(value)) {
      return true
    }
    if (_.isNumber(value)) {
      return true
    }
    return lang.validator.nonEmpty
  },
  alphanumeric: (_field, value) => {
    if (validator.isAlphanumeric(String(value))) {
      return true
    }
    return lang.validator.alphanumeric
  },
  alphanumericWithAccents: (_field, value) => {
    if (/[A-zÀ-ÿ0-9\\.\s]$/.test(String(value))) {
      return true
    }
    return lang.validator.alphanumeric
  },
  url: (_field, value) => {
    if (validator.isURL(trim(value) as string)) {
      return true
    }
    return lang.validator.url
  },
  phoneNumber: (_field, value) => {
    if (phoneNumberRegex.test(trim(value) as string)) {
      return true
    }
    return lang.validator.phoneNumber
  },
  postalCode: (_field, value) => {
    if (validator.isLength(trim(value) as string, { min: 3 })) {
      return true
    }
    return lang.validator.postalCode
  },
  length160: (_field, value) => {
    if (validator.isLength(String(value), { min: 0, max: 160 })) {
      return true
    }
    return lang.validator.length
  },
  length256: (_field, value) => {
    if (validator.isLength(String(value), { min: 0, max: 256 })) {
      return true
    }
    return lang.validator.length256
  },
  lengthMin8: (_field, value) => {
    if (validator.isLength(String(value), { min: 8 })) {
      return true
    }
    return lang.validator.lengthMin8
  },
  number: (_field, value) => {
    if (numberRegex.test(trim(value) as string)) {
      return true
    }
    return lang.validator.number
  },
  date: (_field, value) => {
    if (isValid(parseISO(String(value)))) {
      return true
    }
    return lang.validator.date
  },
}

export default validations

export interface ValidateFieldParams {
  field: string
  value: unknown
  fieldValidations:
    | string
    | number
    | Record<string, unknown>
    | ((params: {
        validations: Record<string, ValidationFn>
        field: string
        value: unknown
        input: Record<string, { value: unknown; validations?: unknown }>
        index?: number
      }) => string | null)
  input?: Record<string, { value: unknown; validations?: unknown }>
  index?: number
}

export type ValidationResult = string | ValidationResult[] | null

export const validateField = ({
  field,
  value,
  fieldValidations,
  input,
  index,
}: ValidateFieldParams): ValidationResult => {
  if (
    _.isObject(fieldValidations) &&
    (fieldValidations as Record<string, unknown>).self !== undefined
  ) {
    const fv = fieldValidations as Record<string, unknown>
    const { self } = fv
    const children = _.omit(fv, ['self', 'errorMessage'])

    const parentValidation = validateField({ field, value, fieldValidations: self as string })
    if (parentValidation !== null) {
      return (
        (_.get(fieldValidations, 'errorMessage', parentValidation) as string) ?? parentValidation
      )
    }

    const validationResults: Record<string, ValidationResult>[] = []
    if (!_.isEmpty(children)) {
      _.each(value as Record<string, unknown>[], (childValue, idx) => {
        const childResult: Record<string, ValidationResult> = {}
        _.each(children as Record<string, unknown>, (childRule, childKey) => {
          childResult[childKey] = validateField({
            field: childKey,
            value: (childValue as Record<string, unknown>)[childKey],
            fieldValidations: childRule as ValidateFieldParams['fieldValidations'],
            index: idx,
            input,
          })
        })
        validationResults.push(childResult)
      })
    }

    if (_.isEmpty(_.filter(validationResults, (result) => !_.isEmpty(_.filter(result))))) {
      return null
    }
    return validationResults as unknown as ValidationResult
  }

  if (_.isFunction(fieldValidations)) {
    return fieldValidations({ validations, field, value, input: input ?? {}, index: index ?? 0 })
  }

  const validationNames = _.isString(fieldValidations) ? fieldValidations.split(' ') : []
  let error: ValidationResult = null
  let isOptional = false
  _.each(validationNames, (validationName) => {
    if (validationName === optionalValidationString) {
      isOptional = true
      return true
    }

    if (
      isOptional &&
      (_.isNull(value) || _.isUndefined(value) || value === '' || _.isEmpty(value))
    ) {
      isOptional = false
      return true
    }

    if (isOptional) {
      isOptional = false
    }

    const validationResult = validations[validationName]?.(field, value)
    if (validationResult !== true) {
      error = validationResult
      return false
    }
  })

  return error
}

export interface FieldValidationArg {
  value: unknown
  validations?: string | number | Record<string, unknown>
}

export const validateFields = (
  args: Record<string, FieldValidationArg>,
): Record<string, ValidationResult> => {
  const errors: Record<string, ValidationResult> = {}

  _.each(args, (value, key) => {
    if (value.validations) {
      const error = validateField({
        field: key,
        value: value.value,
        fieldValidations: value.validations,
        input: args,
      })

      if (error) {
        errors[key] = error
      }
    }
  })

  return errors
}
